import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Row, Col, Badge, Button, Alert, Table, Modal, Form } from 'react-bootstrap';
import { Icon } from '@iconify/react';
import api from '../lib/api';
import { useAuth } from '../lib/auth.jsx';
import PageHeader from '../components/PageHeader.jsx';
import { fmtDate, fmtMoney } from '../lib/format.js';

const STATUS_LABELS = {
  draft:             { label: 'Entwurf',         variant: 'secondary' },
  pending_approval:  { label: 'Freigabe ausstehend', variant: 'warning' },
  approved:          { label: 'Freigegeben',     variant: 'info' },
  fulfilled:         { label: 'Erfuellt',        variant: 'success' },
  cancelled:         { label: 'Storniert',       variant: 'danger' },
  suspended:         { label: 'Pausiert',        variant: 'dark' }
};

function ActionModal({ show, onClose, title, reasonRequired, helpText, onSubmit, busy }) {
  const [reason, setReason] = useState('');
  const submit = () => {
    if (reasonRequired && !reason.trim()) return;
    onSubmit(reason.trim() || null);
    setReason('');
  };
  return (
    <Modal show={show} onHide={onClose} backdrop="static">
      <Modal.Header closeButton><Modal.Title>{title}</Modal.Title></Modal.Header>
      <Modal.Body>
        {helpText && <Alert variant="info" className="small">{helpText}</Alert>}
        <Form.Label>Begruendung {reasonRequired ? '(erforderlich)' : '(optional)'}</Form.Label>
        <Form.Control as="textarea" rows={3} value={reason} onChange={e => setReason(e.target.value)} />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onClose}>Abbrechen</Button>
        <Button onClick={submit} disabled={busy || (reasonRequired && !reason.trim())}>
          {busy ? 'Speichere…' : 'Bestaetigen'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default function OrderDetailPage() {
  const { id } = useParams();
  const auth = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [actionType, setActionType] = useState(null); // 'approve' | 'reject' | 'cancel'
  const [error, setError] = useState(null);

  // useQueries fuer parallel-load Order + Features + Customer (vermeidet Waterfall)
  const [orderQ, featuresQ] = useQueries({
    queries: [
      { queryKey: ['order', id], queryFn: () => api.get(`/orders/${id}`).then(r => r.data.order) },
      { queryKey: ['features-with-tiers'], queryFn: () => api.get('/features', { params: { with_tiers: true } }).then(r => r.data.items) }
    ]
  });

  const order = orderQ.data;
  const features = featuresQ.data || [];
  const featureById = Object.fromEntries(features.map(f => [f.id, f]));

  const customerQ = useQueries({
    queries: order ? [{ queryKey: ['customer', order.customer_id], queryFn: () => api.get(`/customers/${order.customer_id}`).then(r => r.data.customer || r.data) }] : []
  })[0];
  const customer = customerQ?.data;

  const subscriptionQ = useQueries({
    queries: order?.subscription_id ? [{ queryKey: ['subscription', order.subscription_id], queryFn: () => api.get(`/subscriptions/${order.subscription_id}`).then(r => r.data.subscription || r.data) }] : []
  })[0];
  const subscription = subscriptionQ?.data;

  const mutate = useMutation({
    mutationFn: ({ action, reason }) => {
      const body = action === 'approve' ? { approver_user_id: auth.user?.id, reason }
        : { decider_user_id: auth.user?.id, reason };
      return api.post(`/orders/${id}/${action}`, body).then(r => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['order', id] });
      qc.invalidateQueries({ queryKey: ['orders'] });
      setActionType(null);
      setError(null);
    },
    onError: (err) => setError(err.response?.data?.message || JSON.stringify(err.response?.data?.details || err.message))
  });

  if (orderQ.isLoading) return <div className="p-4 text-muted">Lade…</div>;
  if (orderQ.error) return <Alert variant="danger" className="m-3">Fehler: {orderQ.error.message}</Alert>;
  if (!order) return <Alert variant="warning" className="m-3">Bestellung nicht gefunden.</Alert>;

  const meta = STATUS_LABELS[order.status] || { label: order.status, variant: 'secondary' };
  const canApprove = auth.can('orders', 'approve');
  const canReject = auth.can('orders', 'reject');
  const canCancel = auth.can('orders', 'cancel');
  const isPending = order.status === 'pending_approval';
  const isCancellable = ['draft', 'pending_approval', 'approved', 'fulfilled'].includes(order.status);

  return (
    <>
      <PageHeader
        title={`Bestellung ${order.order_number}`}
        subtitle={<Badge bg="" className={`badge-soft-${meta.variant}`}>{meta.label}</Badge>}
        actions={
          <Button variant="outline-secondary" onClick={() => navigate('/orders')}>
            <Icon icon="tabler:arrow-left" className="me-1" />Zurueck zur Liste
          </Button>
        }
      />

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Row>
        <Col md={8}>
          <Card className="card-flat mb-3">
            <Card.Header>Items</Card.Header>
            <Card.Body className="p-0">
              <Table className="mb-0">
                <thead><tr><th>Feature</th><th>Menge</th><th>Einzelpreis</th></tr></thead>
                <tbody>
                  {(order.configurator_input?.items || []).map((it, i) => {
                    const f = featureById[it.feature_id];
                    return (
                      <tr key={i}>
                        <td>{f?.name || <span className="text-muted">{it.feature_id}</span>}</td>
                        <td>{it.quantity || 1}</td>
                        <td>{f ? fmtMoney(f.price_cents) : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Card.Body>
          </Card>

          {order.status_history?.length > 0 && (
            <Card className="card-flat mb-3">
              <Card.Header>Status-Verlauf</Card.Header>
              <Card.Body>
                <ul className="list-unstyled mb-0">
                  {order.status_history.map((h, i) => (
                    <li key={i} className="mb-2">
                      <span className="small text-muted me-2">{fmtDate(h.at)}</span>
                      <Badge bg="" className="badge-soft-secondary me-1">{h.from}</Badge>
                      <Icon icon="tabler:arrow-right" className="mx-1" />
                      <Badge bg="" className="badge-soft-primary me-2">{h.to}</Badge>
                      {h.reason && <span className="small">{h.reason}</span>}
                      {h.by_user_id && <span className="small text-muted ms-2">durch {h.by_user_id}</span>}
                    </li>
                  ))}
                </ul>
              </Card.Body>
            </Card>
          )}
        </Col>

        <Col md={4}>
          <Card className="card-flat mb-3">
            <Card.Header>Kunde</Card.Header>
            <Card.Body>
              {customer ? (
                <>
                  <div className="fw-semibold">
                    <Link to={`/customers/${customer.id}`}>{customer.name}</Link>
                  </div>
                  <div className="small text-muted">{customer.customer_number}</div>
                  {customer.contact_email && <div className="small mt-1">{customer.contact_email}</div>}
                </>
              ) : <span className="text-muted">Lade…</span>}
            </Card.Body>
          </Card>

          {order.license_id && (
            <Card className="card-flat mb-3">
              <Card.Header>Lizenz</Card.Header>
              <Card.Body>
                <Link to={`/licenses/${order.license_id}`}>
                  <Icon icon="tabler:certificate" className="me-1" />Lizenz oeffnen
                </Link>
              </Card.Body>
            </Card>
          )}

          {subscription && (
            <Card className="card-flat mb-3">
              <Card.Header>Abo</Card.Header>
              <Card.Body>
                <div className="small">Naechste Rechnung: {fmtDate(subscription.next_invoice_at)}</div>
                <div className="small text-muted">
                  alle {subscription.interval_count} {subscription.interval_unit}
                </div>
              </Card.Body>
            </Card>
          )}

          <Card className="card-flat mb-3">
            <Card.Header>Aktionen</Card.Header>
            <Card.Body className="d-grid gap-2">
              {canApprove && isPending && (
                <Button variant="success" onClick={() => setActionType('approve')}>
                  <Icon icon="tabler:check" className="me-1" />Freigeben
                </Button>
              )}
              {canReject && isPending && (
                <Button variant="warning" onClick={() => setActionType('reject')}>
                  <Icon icon="tabler:x" className="me-1" />Ablehnen
                </Button>
              )}
              {canCancel && isCancellable && (
                <Button variant="outline-danger" onClick={() => setActionType('cancel')}>
                  <Icon icon="tabler:circle-x" className="me-1" />Stornieren
                </Button>
              )}
              {!isPending && !isCancellable && (
                <span className="small text-muted">Keine Aktionen verfuegbar (Status: {meta.label})</span>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ActionModal
        show={actionType === 'approve'}
        onClose={() => setActionType(null)}
        title="Bestellung freigeben"
        reasonRequired={false}
        helpText="Die Lizenz wird erzeugt und per E-Mail an den Kunden gesendet."
        onSubmit={(reason) => mutate.mutate({ action: 'approve', reason })}
        busy={mutate.isLoading}
      />
      <ActionModal
        show={actionType === 'reject'}
        onClose={() => setActionType(null)}
        title="Bestellung ablehnen"
        reasonRequired={true}
        helpText="Begruendung wird dem Bestelluser per E-Mail mitgeteilt."
        onSubmit={(reason) => mutate.mutate({ action: 'reject', reason })}
        busy={mutate.isLoading}
      />
      <ActionModal
        show={actionType === 'cancel'}
        onClose={() => setActionType(null)}
        title="Bestellung stornieren"
        reasonRequired={false}
        onSubmit={(reason) => mutate.mutate({ action: 'cancel', reason })}
        busy={mutate.isLoading}
      />
    </>
  );
}
